import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  useColorModeValue,
  Card,
  CardBody,
  Badge,
  HStack,
  VStack,
  Button,
  Progress,
  Icon,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "../../../axiosConfig";

const OrgDashboard = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalSurveys: 0,
    totalRespondents: 0,
    completedSurveys: 0,
    completionRate: 0,
    responseTrends: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get("/organizations/me/stats"); // API call for Hub stats
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast({
          title: t("hub.dashboard.error"),
          description: t("hub.dashboard.errorMessage"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchStats();
  }, [toast, t]);

  const totalSurveys = Number(stats?.totalSurveys) || 0;
  const totalRespondents = Number(stats?.totalRespondents) || 0;
  const completedSurveys = Number(stats?.completedSurveys) || 0;
  const completionRate = Number(stats?.completionRate) || 0;

  const isEarlyStage =
    totalSurveys <= 0 || totalRespondents <= 0 || completionRate <= 0;

  const getFocusText = (rate) => {
    if (rate <= 0) {
      return "You're ready to launch your first survey. Start by configuring your survey and inviting staff.";
    }
    if (rate < 40) {
      return "Participation is low. Consider sending a reminder and confirming staff have the right link.";
    }
    if (rate <= 70) {
      return "Responses are coming in. Monitor participation and plan your results review.";
    }
    return "Strong participation. Get ready to review results and align on priorities.";
  };

  const getHealthStatus = (rate) => {
    if (rate < 40) {
      return { label: "Needs attention", colorScheme: "red" };
    }
    if (rate <= 70) {
      return { label: "In progress", colorScheme: "orange" };
    }
    return { label: "Strong", colorScheme: "green" };
  };

  const healthStatus = getHealthStatus(completionRate);
  const focusText = getFocusText(completionRate);

  const startedCount = totalRespondents;
  const completedCount = Math.min(completedSurveys, startedCount);
  const completedFromStartedPct =
    startedCount > 0 ? Math.round((completedCount / startedCount) * 100) : 0;
  const cardBorderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");
  const focusCardBg = useColorModeValue("blue.50", "blue.900");
  const focusCardTextColor = useColorModeValue("gray.700", "gray.100");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box p={6}>
      {/* Welcome Message */}
      <Heading as="h1" size="xl">
        {t("hub.dashboard.welcome")}
      </Heading>
      <Text fontSize="lg" mt={2}>
        {t("hub.dashboard.overview")}
      </Text>

      {/* Top Panel */}
      <Card
        mt={6}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorderColor}
        shadow="sm"
        bg={cardBg}
      >
        <CardBody>
          {isEarlyStage ? (
            <VStack align="start" spacing={4}>
              <Box>
                <Heading as="h2" size="md">
                  Welcome to your improvement dashboard
                </Heading>
                <Text color={secondaryTextColor} mt={2}>
                  This is where you launch surveys, invite staff, and review
                  results. Follow these steps to build participation momentum.
                </Text>
              </Box>

              <VStack align="start" spacing={2}>
                {[
                  "Configure your survey",
                  "Invite participants",
                  "Monitor participation",
                  "Review results",
                ].map((item) => (
                  <HStack key={item} spacing={2}>
                    <Icon as={CheckCircleIcon} color="green.500" />
                    <Text>{item}</Text>
                  </HStack>
                ))}
              </VStack>

              <HStack spacing={3} flexWrap="wrap">
                <Button colorScheme="blue" onClick={() => navigate("/hub/surveys")}>
                  Configure survey
                </Button>
                <Button variant="outline" onClick={() => navigate("/hub/results")}>
                  View results
                </Button>
              </HStack>
            </VStack>
          ) : (
            <Box>
              <Heading as="h2" size="md">
                Current focus
              </Heading>
              <Text color={secondaryTextColor} mt={2}>
                {focusText}
              </Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Stats Section */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mt={6}>
        <Card
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorderColor}
          shadow="sm"
          bg={cardBg}
        >
          <CardBody>
            <Stat>
              <StatLabel>Active improvement cycles</StatLabel>
              <StatNumber fontSize="4xl">{totalSurveys}</StatNumber>
              <StatHelpText color={mutedTextColor}>
                Surveys currently available for your organization.
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorderColor}
          shadow="sm"
          bg={cardBg}
        >
          <CardBody>
            <Stat>
              <StatLabel>Staff engaged</StatLabel>
              <StatNumber fontSize="4xl">{totalRespondents}</StatNumber>
              <StatHelpText color={mutedTextColor}>
                Staff who have started responding.
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorderColor}
          shadow="sm"
          bg={cardBg}
        >
          <CardBody>
            <Stat>
              <StatLabel>Responses completed</StatLabel>
              <StatNumber fontSize="4xl">{completedSurveys}</StatNumber>
              <StatHelpText color={mutedTextColor}>
                Completed responses submitted so far.
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorderColor}
          shadow="sm"
          bg={cardBg}
        >
          <CardBody>
            <Stat>
              <StatLabel>Participation health</StatLabel>
              <StatNumber fontSize="4xl">{completionRate}%</StatNumber>
              <StatHelpText>
                <HStack spacing={2}>
                  <Badge colorScheme={healthStatus.colorScheme} borderRadius="full" px={2}>
                    {healthStatus.label}
                  </Badge>
                  <Text color={mutedTextColor}>
                    Completion rate across responses.
                  </Text>
                </HStack>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Current Focus Insight */}
      <Card
        mt={6}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorderColor}
        shadow="sm"
        bg={focusCardBg}
      >
        <CardBody>
          <Heading as="h3" size="sm" mb={2}>
            Current focus
          </Heading>
          <Text color={focusCardTextColor}>{focusText}</Text>
        </CardBody>
      </Card>

      {/* Participation Funnel */}
      <Card
        mt={6}
        p={4}
        shadow="sm"
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorderColor}
        bg={cardBg}
      >
        <CardBody p={0}>
          <Heading as="h3" size="md" mb={2}>
            Participation funnel
          </Heading>
          <Text color={secondaryTextColor} mb={5}>
            Based on currently available participation data.
          </Text>

          <VStack align="stretch" spacing={5}>
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold">Started</Text>
                <Text color={mutedTextColor}>
                  {startedCount}
                </Text>
              </HStack>
              <Progress value={startedCount > 0 ? 100 : 0} size="sm" borderRadius="full" />
            </Box>

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold">Completed</Text>
                <Text color={mutedTextColor}>
                  {completedCount}
                </Text>
              </HStack>
              <Progress
                value={completedFromStartedPct}
                size="sm"
                borderRadius="full"
                colorScheme={healthStatus.colorScheme}
              />
              <Text mt={2} fontSize="sm" color={mutedTextColor}>
                {completedFromStartedPct}% of started responses are complete.
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default OrgDashboard;
